/* AutosaveBadge — "Saved · 2 min ago" indicator (iter 96e).
 *
 * Reads the editor's last-saved timestamp + renders a small status pill.
 * Auto-updates the relative time every 15s. Three states:
 *   - 'saving' → pulsing dot + "Saving…"
 *   - 'saved'  → static dot + "Saved · {relative}"
 *   - 'error'  → red dot + "Save failed"
 *   - default (no lastSavedAt) → "Not yet saved"
 */
import React, { useEffect, useState } from "react";


function fmtRelative(date) {
    if (!date) return "";
    const now = Date.now();
    const then = date instanceof Date ? date.getTime() : new Date(date).getTime();
    const diffSec = Math.max(0, Math.round((now - then) / 1000));
    if (diffSec < 5) return "just now";
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.round(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return new Date(then).toLocaleDateString();
}


export default function AutosaveBadge({ status = "idle", lastSavedAt = null }) {
    /* Tick once per 15s so "30s ago" rolls forward. */
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((n) => n + 1), 15000);
        return () => clearInterval(id);
    }, []);

    let dot = "bg-emerald-500";
    let label = "Saved";
    let extra = "";

    if (status === "saving") {
        dot = "bg-amber-500 animate-pulse";
        label = "Saving…";
    } else if (status === "error") {
        dot = "bg-rose-500";
        label = "Save failed";
    } else if (!lastSavedAt) {
        dot = "bg-slate-300";
        label = "Not yet saved";
    } else {
        extra = ` · ${fmtRelative(lastSavedAt)}`;
    }

    return (
        <span
            data-testid="autosave-badge"
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground"
        >
            <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
            {label}
            {extra && <span className="text-muted-foreground/80">{extra}</span>}
        </span>
    );
}
